/**
 * Countries list with English/Greek names and international dial codes.
 * ISO 3166-1 based; includes EU and common countries.
 */
export interface CountryItem {
  code: string;
  nameEn: string;
  nameGr: string;
  /** E.164 country calling code including leading +, e.g. "+30". Empty for Other. */
  dialCode: string;
}

export const GREECE: CountryItem = {
  code: 'GR',
  nameEn: 'Greece',
  nameGr: 'Ελλάδα',
  dialCode: '+30',
};

export const countries: CountryItem[] = [
  GREECE,
  { code: 'DE', nameEn: 'Germany', nameGr: 'Γερμανία', dialCode: '+49' },
  { code: 'FR', nameEn: 'France', nameGr: 'Γαλλία', dialCode: '+33' },
  { code: 'IT', nameEn: 'Italy', nameGr: 'Ιταλία', dialCode: '+39' },
  { code: 'ES', nameEn: 'Spain', nameGr: 'Ισπανία', dialCode: '+34' },
  { code: 'GB', nameEn: 'United Kingdom', nameGr: 'Ηνωμένο Βασίλειο', dialCode: '+44' },
  { code: 'NL', nameEn: 'Netherlands', nameGr: 'Ολλανδία', dialCode: '+31' },
  { code: 'BE', nameEn: 'Belgium', nameGr: 'Βέλγιο', dialCode: '+32' },
  { code: 'AT', nameEn: 'Austria', nameGr: 'Αυστρία', dialCode: '+43' },
  { code: 'CH', nameEn: 'Switzerland', nameGr: 'Ελβετία', dialCode: '+41' },
  { code: 'PT', nameEn: 'Portugal', nameGr: 'Πορτογαλία', dialCode: '+351' },
  { code: 'SE', nameEn: 'Sweden', nameGr: 'Σουηδία', dialCode: '+46' },
  { code: 'PL', nameEn: 'Poland', nameGr: 'Πολωνία', dialCode: '+48' },
  { code: 'RO', nameEn: 'Romania', nameGr: 'Ρουμανία', dialCode: '+40' },
  { code: 'CZ', nameEn: 'Czech Republic', nameGr: 'Τσεχία', dialCode: '+420' },
  { code: 'HU', nameEn: 'Hungary', nameGr: 'Ουγγαρία', dialCode: '+36' },
  { code: 'BG', nameEn: 'Bulgaria', nameGr: 'Βουλγαρία', dialCode: '+359' },
  { code: 'HR', nameEn: 'Croatia', nameGr: 'Κροατία', dialCode: '+385' },
  { code: 'SK', nameEn: 'Slovakia', nameGr: 'Σλοβακία', dialCode: '+421' },
  { code: 'SI', nameEn: 'Slovenia', nameGr: 'Σλοβενία', dialCode: '+386' },
  { code: 'IE', nameEn: 'Ireland', nameGr: 'Ιρλανδία', dialCode: '+353' },
  { code: 'DK', nameEn: 'Denmark', nameGr: 'Δανία', dialCode: '+45' },
  { code: 'FI', nameEn: 'Finland', nameGr: 'Φινλανδία', dialCode: '+358' },
  { code: 'NO', nameEn: 'Norway', nameGr: 'Νορβηγία', dialCode: '+47' },
  { code: 'CY', nameEn: 'Cyprus', nameGr: 'Κύπρος', dialCode: '+357' },
  { code: 'MT', nameEn: 'Malta', nameGr: 'Μάλτα', dialCode: '+356' },
  { code: 'EE', nameEn: 'Estonia', nameGr: 'Εσθονία', dialCode: '+372' },
  { code: 'LV', nameEn: 'Latvia', nameGr: 'Λετονία', dialCode: '+371' },
  { code: 'LT', nameEn: 'Lithuania', nameGr: 'Λιθουανία', dialCode: '+370' },
  { code: 'US', nameEn: 'United States', nameGr: 'Ηνωμένες Πολιτείες', dialCode: '+1' },
  { code: 'CA', nameEn: 'Canada', nameGr: 'Καναδάς', dialCode: '+1' },
  { code: 'AU', nameEn: 'Australia', nameGr: 'Αυστραλία', dialCode: '+61' },
  { code: 'JP', nameEn: 'Japan', nameGr: 'Ιαπωνία', dialCode: '+81' },
  { code: 'CN', nameEn: 'China', nameGr: 'Κίνα', dialCode: '+86' },
  { code: 'RU', nameEn: 'Russia', nameGr: 'Ρωσία', dialCode: '+7' },
  { code: 'TR', nameEn: 'Turkey', nameGr: 'Τουρκία', dialCode: '+90' },
  { code: 'UA', nameEn: 'Ukraine', nameGr: 'Ουκρανία', dialCode: '+380' },
  { code: 'IL', nameEn: 'Israel', nameGr: 'Ισραήλ', dialCode: '+972' },
  { code: 'BR', nameEn: 'Brazil', nameGr: 'Βραζιλία', dialCode: '+55' },
  { code: 'AR', nameEn: 'Argentina', nameGr: 'Αργεντινή', dialCode: '+54' },
  { code: 'IN', nameEn: 'India', nameGr: 'Ινδία', dialCode: '+91' },
  { code: 'ZA', nameEn: 'South Africa', nameGr: 'Νότια Αφρική', dialCode: '+27' },
  { code: 'EG', nameEn: 'Egypt', nameGr: 'Αίγυπτος', dialCode: '+20' },
  { code: 'MX', nameEn: 'Mexico', nameGr: 'Μεξικό', dialCode: '+52' },
  { code: 'OTHER', nameEn: 'Other', nameGr: 'Άλλο', dialCode: '' },
];

/** Dial-code options for phone inputs (excludes Other / empty). Greece first. */
export const dialCodeCountries: CountryItem[] = countries.filter(c => c.dialCode);

/**
 * Split a stored phone into dial code + national number.
 * Prefers the longest matching dial code; defaults to Greece when empty/unmatched.
 */
export function splitPhoneNumber(phone: string | undefined | null): {
  dialCode: string;
  nationalNumber: string;
  countryCode: string;
} {
  const raw = (phone || '').trim();
  if (!raw) {
    return { dialCode: GREECE.dialCode, nationalNumber: '', countryCode: GREECE.code };
  }

  const normalized = raw.replace(/[\s\-()]/g, '');
  const withPlus = normalized.startsWith('+') ? normalized : `+${normalized.replace(/^\+/, '')}`;

  const sorted = [...dialCodeCountries].sort((a, b) => b.dialCode.length - a.dialCode.length);
  const match = sorted.find(c => withPlus.startsWith(c.dialCode));
  if (match) {
    return {
      dialCode: match.dialCode,
      nationalNumber: withPlus.slice(match.dialCode.length),
      countryCode: match.code,
    };
  }

  // Digits only / unknown prefix — keep as national under Greece
  return {
    dialCode: GREECE.dialCode,
    nationalNumber: withPlus.replace(/^\+/, ''),
    countryCode: GREECE.code,
  };
}

export function composePhoneNumber(dialCode: string, nationalNumber: string): string {
  const national = (nationalNumber || '').replace(/[^\d]/g, '');
  if (!national) return '';
  const code = (dialCode || GREECE.dialCode).trim() || GREECE.dialCode;
  return `${code}${national}`;
}

/** Small flag emoji from ISO 3166-1 alpha-2 (e.g. GR → 🇬🇷). */
export function countryFlagEmoji(code: string): string {
  const cc = (code || '').trim().toUpperCase();
  if (cc.length !== 2 || cc === 'OT' || code === 'OTHER') return '🌐';
  const A = 0x1f1e6;
  const base = 'A'.charCodeAt(0);
  return String.fromCodePoint(A + (cc.charCodeAt(0) - base), A + (cc.charCodeAt(1) - base));
}
